import React, { useState } from "react";
import { Pencil, Trash2, ImageIcon, ChevronDown } from "lucide-react";
import { resolveMediaUrl } from "../../../utils/media";
import { btnDanger, btnEdit, fileInputClass } from "./adminProductUi";

/**
 * Card row for admin product lists — clearer than cramped tables for non-technical users.
 */
const AdminProductListCard = ({
  id,
  title,
  meta = [],
  status,
  imageUrl,
  isEditing = false,
  onEdit,
  onDelete,
  uploadFile,
  onUploadFileChange,
  onUpload,
  onRemoveImage,
  uploadDisabled = false,
}) => {
  const [imageOpen, setImageOpen] = useState(false);
  const resolvedImage = resolveMediaUrl(imageUrl);
  const isActive = typeof status === "boolean" ? status : null;

  return (
    <article
      className={`rounded-xl border bg-white p-4 transition ${
        isEditing ? "border-blue-400 ring-2 ring-blue-100" : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="shrink-0">
          {resolvedImage ? (
            <img
              src={resolvedImage}
              alt=""
              className="w-full sm:w-28 h-20 object-cover rounded-lg border border-slate-200"
            />
          ) : (
            <div className="w-full sm:w-28 h-20 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-slate-400 gap-1">
              <ImageIcon size={18} />
              <span className="text-xs">No image</span>
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h4 className="font-semibold text-slate-900 truncate">{title || `Item #${id}`}</h4>
              {meta.length ? (
                <p className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                  {meta.map((part, idx) => (
                    <span key={idx}>
                      {idx > 0 ? <span className="text-slate-300 mr-2">·</span> : null}
                      {part}
                    </span>
                  ))}
                </p>
              ) : null}
            </div>
            {isActive !== null ? (
              <span
                className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium ${
                  isActive ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"
                }`}
              >
                {isActive ? "Published" : "Unpublished"}
              </span>
            ) : null}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={onEdit} className={btnEdit}>
              <Pencil size={14} />
              Edit
            </button>
            <button type="button" onClick={onDelete} className={btnDanger}>
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        </div>
      </div>

      {onUploadFileChange || onUpload || onRemoveImage ? (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={() => setImageOpen((v) => !v)}
            className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900"
          >
            <ChevronDown size={16} className={`transition-transform ${imageOpen ? "rotate-180" : ""}`} />
            Change product image
          </button>
          {imageOpen ? (
            <div className="mt-3 space-y-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => onUploadFileChange?.(e.target.files?.[0] || null)}
                className={fileInputClass}
              />
              <div className="flex flex-wrap gap-2">
                {onUpload ? (
                  <button
                    type="button"
                    disabled={uploadDisabled}
                    onClick={onUpload}
                    className="text-sm px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Upload image
                  </button>
                ) : null}
                {onRemoveImage ? (
                  <button type="button" onClick={onRemoveImage} className={btnDanger}>
                    Remove image
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
};

export default AdminProductListCard;
